if (!customElements.get('amb-retail-links')) {
    const MOBILE_BREAKPOINT = 768;
    /**
     * Custom element for managing retail links with responsive behavior
     * Handles both desktop and mobile views with different layouts
     */
    customElements.define(
        'amb-retail-links',
        class AmbRetailLinks extends HTMLElement {
            /**
             * Initialize component and set up required DOM references
             * Tracks viewport state and prepares for mobile/desktop view handling
             */
            constructor() {
                super();
                this.list = this.querySelector('.amb-retail-links__list');
                this.summary = this.querySelector('.amb-retail-links__summary');
                this.clonedList = null;
                this.overlay = null;
                this.isMobileView = window.innerWidth < MOBILE_BREAKPOINT;
                this.resizeTimeout = null;
            }
            /**
             * Lifecycle callback when element is connected to DOM
             * Sets up overlay, initializes mobile view, and attaches event listeners
             */
            connectedCallback() {
                try {
                    if (!this.list || !this.summary) {
                        throw new Error('Required elements not found');
                    }
                    this.createOverlay();
                    this.initMobileView();
                    this.setupEventListeners();
                } catch (error) {
                    console.error('Error initializing retail links:', error);
                }
            }
            /**
             * Lifecycle callback when element is disconnected from DOM
             */
            disconnectedCallback() {
                this.cleanup();
            }
            /**
             * Cleans up component by removing overlay, cloned list,
             * and event listeners to prevent memory leaks
             */
            cleanup() {
                if (this.overlay) {
                    this.overlay.remove();
                    this.overlay = null;
                }
                if (this.clonedList) {
                    this.clonedList.remove();
                    this.clonedList = null;
                }
                window.removeEventListener('resize', this.handleResize);
            }
            /**
             * Creates and sets up a modal overlay for mobile view
             * The overlay appears behind the mobile menu and dismisses it when clicked
             */
            createOverlay() {
                this.overlay = document.createElement('div');
                this.overlay.classList.add('amb-retail-links-overlay');
                this.overlay.innerHTML = '&nbsp;';
                this.overlay.addEventListener('click', () => this.hideClonedList());
                document.body.appendChild(this.overlay);
            }
            /**
             * Sets up event listeners for:
             * - Window resize (with debounce) to handle mobile/desktop view transitions
             * - Summary click to toggle mobile menu
             */
            setupEventListeners() {
                this.handleResize = this.debounce(() => {
                    const newIsMobileView = window.innerWidth < MOBILE_BREAKPOINT;
                    if (this.isMobileView !== newIsMobileView) {
                        this.isMobileView = newIsMobileView;
                        this.initMobileView();
                    }
                }, 250);
                window.addEventListener('resize', this.handleResize);
                this.summary.addEventListener('click', (e) => {
                    if (!this.isMobileView) return;
                    e.preventDefault();
                    this.toggleClonedList();
                });
            }
            /**
             * Sets up close button functionality for mobile menu
             */
            setupCloseButton() {
                if (!this.clonedList) return;
                const closeButton = this.clonedList.querySelector('.btn-close');
                closeButton?.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.hideClonedList();
                });
            }
            /**
             * Toggles visibility of mobile menu based on current state
             */
            toggleClonedList() {
                const isVisible = this.clonedList?.classList.contains('visible');
                isVisible ? this.hideClonedList() : this.showClonedList();
            }
            /**
             * Hides mobile menu and overlay
             */
            hideClonedList() {
                this.clonedList?.classList.remove('visible');
                this.overlay?.classList.remove('visible');
            }
            /**
             * Shows mobile menu and overlay
             */
            showClonedList() {
                this.clonedList?.classList.add('visible');
                this.overlay?.classList.add('visible');
            }
            /**
             * Initializes or updates the mobile/desktop view
             * - For mobile: Creates cloned list with mobile styling if needed
             * - For desktop: Removes mobile-specific elements and shows original list
             */
            initMobileView() {
                if (this.isMobileView) {
                    if (!this.clonedList) {
                        try {
                            this.clonedList = this.list.cloneNode(true);
                            this.clonedList.classList.add('amb-retail-links__list--mobile');
                            document.body.appendChild(this.clonedList);
                            this.setupCloseButton();
                        } catch (error) {
                            console.error('Error creating mobile view:', error);
                            return;
                        }
                    }
                    this.list.style.display = 'none';
                } else {
                    if (this.clonedList) {
                        this.hideClonedList();
                        this.clonedList.remove();
                        this.clonedList = null;
                    }
                    this.list.style.display = 'block';
                }
            }
            /**
             * Utility function to debounce function calls
             * Prevents excessive execution of functions, especially during window resize
             * @param {Function} fn - Function to debounce
             * @param {number} delay - Delay in milliseconds
             * @returns {Function} Debounced function
             */
            debounce(fn, delay) {
                return (...args) => {
                    clearTimeout(this.resizeTimeout);
                    this.resizeTimeout = setTimeout(() => fn.apply(this, args), delay);
                };
            }
        }
    );
}